// frontend/src/services/api/modules/fotoID.js
import { API_URL } from '../config';
import { getAuthHeaders } from '../utils/fetchWrapper';

/**
 * Obtener el estado de Foto-ID de una mascota
 * @param {string} objetoId - ID de la mascota
 * @returns {Promise<Object>} { completo, fotos: { frontal, lateral }, analisis, fechaCreacion }
 */
export const obtenerFotoIDStatus = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/foto-id/${objetoId}/status`, {
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener Foto-ID' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en obtenerFotoIDStatus:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Subir una foto (frontal o lateral) para Foto-ID
 * @param {string} objetoId - ID de la mascota
 * @param {File} archivo - Archivo de imagen
 * @param {string} tipo - 'frontal' o 'lateral'
 * @param {function} onProgress - Callback de progreso (opcional)
 * @returns {Promise<Object>}
 */
export const subirFotoID = async (objetoId, archivo, tipo, onProgress = null) => {
  try {
    const headers = await getAuthHeaders();
    delete headers['Content-Type'];

    const formData = new FormData();
    formData.append('foto', archivo);
    formData.append('tipo', tipo);

    if (onProgress) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              resolve({ error: response.error || 'Error al subir foto' });
            }
          } catch (e) {
            resolve({ error: 'Error al procesar respuesta' });
          }
        });

        xhr.addEventListener('error', () => {
          resolve({ error: 'Error de conexión' });
        });

        xhr.open('POST', `${API_URL}/api/foto-id/${objetoId}/foto`);

        Object.keys(headers).forEach(key => {
          xhr.setRequestHeader(key, headers[key]);
        });

        xhr.send(formData);
      });
    }

    const response = await fetch(`${API_URL}/api/foto-id/${objetoId}/foto`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al subir foto' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en subirFotoID:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Guardar análisis editado manualmente
 * @param {string} objetoId - ID de la mascota
 * @param {Object} analisis - { colorPrincipal, coloresSecundarios, colorOjos, tipoPelaje, manchasDetalladas }
 * @returns {Promise<Object>}
 */
export const guardarAnalisisFotoID = async (objetoId, analisis) => {
  try {
    const headers = await getAuthHeaders();
    headers['Content-Type'] = 'application/json';
    const response = await fetch(`${API_URL}/api/foto-id/${objetoId}/analisis`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(analisis)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al guardar análisis' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en guardarAnalisisFotoID:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Procesar fotos con IA para generar análisis
 * @param {string} objetoId - ID de la mascota
 * @returns {Promise<Object>}
 */
export const procesarFotoID = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/foto-id/${objetoId}/procesar`, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al procesar Foto-ID' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en procesarFotoID:', error);
    return { error: 'Error de conexión' };
  }
};
